/*
 * Copyright (c) 2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import React, { Suspense, useContext, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AccountContext, ContextUpdater } from "./context/ContextProvider";
import Overlay from "./elements/Overlay";
import { isCustomDomain } from "./util/CustomDomain";
import { requestBrandingGet } from "./context/RequestManager";

const Login = React.lazy(() => import("./login/Login"));
const PasswordReset = React.lazy(() => import("./login/PasswordReset"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const RouterStatus = React.lazy(() => import("./pages/router/RouterStatus"));
const DashboardUsers = React.lazy(() => import("./pages/admin/DashboardUsers"));
const MarketingTemplates = React.lazy(() => import("./pages/marketingTemplates/MarketingTemplates"));
const LoginPageContainer = React.lazy(() => import("./pages/loginPage/LoginPageContainer"));
const Provisioning = React.lazy(() => import("./provisioning/Provisioning"));
const ClientDashboard = React.lazy(() => import("./pages/client/ClientDashboard"));
const UserSearch = React.lazy(() => import("./pages/search/UserSearch"));
const ClientMarketing = React.lazy(() => import("./pages/marketing/ClientMarketing"));
const IntegrationsHome = React.lazy(() => import("./pages/marketing/integrations/IntegrationsHome"));
const BusinessClientInfo = React.lazy(() => import("./pages/admin/BusinessClientInfo"));
const ClientSettings = React.lazy(() => import("./pages/settings/ClientSettings"));
const WifiSettings = React.lazy(() => import("./pages/settings/WifiSettings"))
const BrandSettings = React.lazy(() => import("./pages/settings/BrandSettings"));
const DashboardAccount = React.lazy(() => import("./pages/account/DashboardAccount"));
const CaptivePortalPage = React.lazy(() => import("./pages/captivePortal/CaptivePortalPage"));
const OauthPopupWindow = React.lazy(() => import("./pages/client/OauthPopupWindow"));
const AdminBillingManagement = React.lazy(() => import ("./pages/admin/AdminBillingManagement"));
const AdminBrandingManagement = React.lazy(() => import ("./pages/admin/AdminBrandingManagement"))

const Router = () => {
  // verify credentials
  const context = useContext(AccountContext);
  const updateContexts = useContext(ContextUpdater);
  const location = useLocation();
  const navigate = useNavigate();

  const loggedIn = context.token != null;
  const admin = context.admin ?? false;
  const subAdmin = context.subAdmin ?? false;

  useEffect(() => {
    const noAuthRoutes = [
      "/login",
      "/password-reset",
      "/captiveportal",
      "/captiveportalpreview",
      "/authorize-callback",
    ];

    const hashFragments = [
      "#subscription"
    ];

    if (!loggedIn && !noAuthRoutes.some((route) => route === location.pathname)) {
      const hashFragment = location.hash;
      if (hashFragments.some((hash) =>  hash === hashFragment)) {
        navigate(`/login${hashFragment}`);
      } else{
        navigate("/login")
      }
    } else if (isCustomDomain()) {
      // Check if the domains configuration is complete
      const domainBrand = context.domainBrand;
      if (domainBrand && !domainBrand.configurationComplete && (admin || subAdmin)) {
        requestBrandingGet({
          context: updateContexts,
          token: context.token,
          id: domainBrand.id,
          domainBrand: true,
          navigate: (_) => "/brand-settings"
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // decide default pages
  const Index = loggedIn ? (
    admin ? AdminDashboard : ClientDashboard
  ) : Login;

  return (
    <Routes>
      <Route path="/" element={<Overlay />}>
        <Route index element={<Index />} />

        {/* Login */}
        <Route path="login" element={<Login />} />
        <Route path="password-reset" element={<Suspense><PasswordReset /></Suspense>} />

        {/* Admin */}
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="router-status" element={<RouterStatus />} />
        <Route path="dashboard-users" element={<DashboardUsers />} />
        <Route path="marketing-templates" element={<MarketingTemplates />} />
        <Route path="admin-billing" element={<AdminBillingManagement />} />
        <Route path="admin-branding" element={<AdminBrandingManagement />} />
        <Route path="login-pages" element={<LoginPageContainer onUpload={() => navigate("/admin")} />} />
        <Route path="provision" element={<Provisioning />} />

        {/* Client */}
        <Route path="dashboard" element={<ClientDashboard key={context.clientId} />} />
        <Route path="guests" element={<UserSearch />} />
        <Route path="login-page" element={<BusinessClientInfo title="Login Pages" singleId="login" />} />
        <Route path="marketing" element={<ClientMarketing />} />
        <Route path="integrations" element={<IntegrationsHome />} />
        <Route path="wifi-settings" element={<WifiSettings />} />
        <Route path="business-settings" element={<ClientSettings />} />
        <Route path="brand-settings" element={<BrandSettings />} />

        {/* Account */}
        <Route path="dashboard-account" element={<DashboardAccount />} />

        {/* Login Page */}
        <Route path="captiveportal" element={<CaptivePortalPage />} />
        <Route path="captiveportalpreview" element={<CaptivePortalPage preview />} />

        {/* Other */}
        <Route path="authorize-callback" element={<OauthPopupWindow />} />
      </Route>
    </Routes>
  );
};

export default Router;